import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../../../app/hooks';
import { useEnsureReconciliations } from './useEnsureReconciliations';
import { selectReconciliationsEntry } from '../../../../../store/reconciliations/selectors';

/**
 * Drives the drawer's own data needs: the page-load (default) fetch, plus a second fetch only
 * when the user picks a FY set that differs from the server's default (D26-6/A-62). Selection
 * state is local — it only needs to live as long as the drawer session, and the redux cache
 * already keys every fys set the user visits, so there's nothing extra to persist here (KISS).
 */
export function useReconciliationsLogic(orgId: string | undefined) {
    const defaultEntry = useAppSelector((state) => selectReconciliationsEntry(state, orgId ?? '', null));
    useEnsureReconciliations(orgId, null);

    const defaultFys = defaultEntry?.payload?.fys ?? [];
    const availableFys = defaultEntry?.payload?.availableFys ?? [];

    // Stored with the org it was made for, and read back only for that org. Nothing in this
    // hook's tree remounts on an `orgId` route change — not Dashboard, not TabSelector, not the
    // sidebar chip — so a plain useState would carry the previous company's years into the very
    // render that computes `effectiveFys` and dispatches its fetch. A reset effect cannot reach
    // that render; deriving the reset can.
    const [selection, setSelection] = useState<{ orgId: string | undefined; fys: number[] }>({ orgId, fys: [] });
    const selectedFys = selection.orgId === orgId ? selection.fys : [];
    const setSelectedFys = useCallback((fys: number[]) => setSelection({ orgId, fys }), [orgId]);

    const defaultFysKey = defaultFys.join(',');
    useEffect(() => {
        // Adopt the server's default selection once it lands, if the user hasn't chosen yet.
        if (defaultFys.length && selectedFys.length === 0) setSelectedFys(defaultFys);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFysKey, orgId]);

    const effectiveFys = selectedFys.length ? selectedFys : null;
    useEnsureReconciliations(orgId, effectiveFys);
    const entry = useAppSelector((state) => selectReconciliationsEntry(state, orgId ?? '', effectiveFys));

    return {
        entry: effectiveFys ? entry : defaultEntry,
        availableFys,
        selectedFys: effectiveFys ?? defaultFys,
        setSelectedFys,
    };
}
